import { useEffect, useState } from 'react';
import type { PlatformUserRow, SearchPlatformUsersInput } from '@etiquetachef/types';
import { searchPlatformUsersCallable } from '@/lib/callables';

const TAG = 'admin/platformUsers/search';

export type UsersSearchState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; items: PlatformUserRow[]; total: number; truncated: boolean }
  | { kind: 'ERROR'; error: unknown };

/**
 * Busca server-side da lista global de Usuários (callable
 * `searchPlatformUsers`, issue #257). Espelha `useTenantsSearch`: sem debounce
 * (o PropertyFilter só emite `onChange` ao adicionar/remover token) e com
 * `refreshNonce` para re-fetch manual, já que a busca não é `onSnapshot`.
 *
 * O `refreshNonce` também é o que atualiza a lista depois de uma ação —
 * verificar/desverificar/bloquear mudam Auth e Firestore, e nenhum dos dois
 * chega aqui sozinho.
 *
 * Origem: #257 — commit b3bc5df63, 2026-08-11 17:08 +0200 (tela global de
 * Usuários).
 *
 * Contrato: `LOADING` → `READY` (`items` da página, `total` e `truncated`
 * quando o servidor bateu o teto de agregação) | `ERROR`. Resposta antiga
 * que chega depois de uma nova é descartada (`cancelled`).
 *
 * Usado por: `routes/UsersPage.tsx`. Spec: `docs/screens/platform-users-admin.md`.
 *
 * Dados: input `SearchPlatformUsersInput` (`tokens`, `page`, `pageSize`),
 * saída `SearchPlatformUsersResult` — `packages/types/src/platform-user-search.ts`.
 *
 * Falhas conhecidas: o `catch` só faz `console.error` — sem report para a
 * telemetria (ver #926, achados).
 *
 * Issues: #257.
 */
export function useUsersSearch(
  input: SearchPlatformUsersInput,
  refreshNonce: number,
): UsersSearchState {
  const [state, setState] = useState<UsersSearchState>({ kind: 'LOADING' });
  // Serializa o input p/ dep estável do efeito (o objeto muda de identidade a
  // cada render). A string cobre tokens + page + pageSize.
  const key = JSON.stringify(input);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'LOADING' });
    searchPlatformUsersCallable(input)
      .then((r) => {
        if (!cancelled) {
          setState({ kind: 'READY', items: r.items, total: r.total, truncated: r.truncated });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error(`${TAG} failed`, err);
        setState({ kind: 'ERROR', error: err });
      });
    return (): void => {
      cancelled = true;
    };
    // `key` cobre `input`; incluí-lo direto re-dispararia a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshNonce]);

  return state;
}
